import type { HttpError } from '@refinedev/core'
import { useOne, useShow } from '@refinedev/core'
import type {
  Cabinet,
  RoundDetail,
  RoundLedgerEntry,
  SpinProof,
} from '@sirius/contracts/admin'
import { gambleSymbolToColour } from '@sirius/contracts/admin'
import type { TFunction } from 'i18next'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { ShowView, ShowViewHeader } from '~/components/refineUi/views/showView'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Table as UiTable,
} from '~/components/ui/table'
import {
  formatCredits,
  formatDateTime,
  formatNumber,
  formatOptionalDateTime,
  formatSignedCredits,
} from '~/i18n/format'
import { cn } from '~/lib/utils'
import { useGameOptions } from '~/pages/cabinets/useGameOptions'
import { versionLabel } from '~/pages/games/paths'
import { NotFoundPage } from '~/pages/notFound'
import { ROUNDS_RESOURCE } from '~/providers/dataProvider'
import type { GambleStep, SpinPlay } from './roundFormat'
import {
  cellKey,
  featureFactOf,
  freeGameLabel,
  gridRows,
  pendingWin,
  progressText,
  roundSteps,
  stakeWithTotalText,
  winningCells,
} from './roundFormat'
import { RoundStatusBadge } from './roundStatusBadge'

type Paylines = RoundDetail['paylines']

function CabinetName({ id }: { id: string }) {
  const { t } = useTranslation()
  const { result, query } = useOne<Cabinet, HttpError>({
    resource: 'cabinets',
    id,
  })
  return (
    <Link
      className="underline"
      to={`/cabinets/${id}`}
    >
      {query.isError ? t('roundsPage.show.theCabinet') : (result?.name ?? '…')}
    </Link>
  )
}

/** A titled part of the page: a region named by its heading. */
function Section({
  id,
  title,
  level = 3,
  children,
}: {
  id: string
  title: string
  level?: 3 | 4
  children: ReactNode
}) {
  const Heading = level === 3 ? 'h3' : 'h4'
  return (
    <section
      aria-labelledby={id}
      className="space-y-3"
    >
      <Heading
        id={id}
        className={level === 3 ? 'font-semibold' : 'text-sm font-semibold'}
      >
        {title}
      </Heading>
      {children}
    </section>
  )
}

/** A `<dl>` of label/value pairs, as on the other show pages. */
function Facts({ items }: { items: [string, ReactNode][] }) {
  return (
    <dl className="grid max-w-md grid-cols-[10rem_1fr] gap-2 text-sm">
      {items.map(([label, value]) => (
        <div
          key={label}
          className="contents"
        >
          <dt className="text-muted-foreground">{label}</dt>
          <dd className="tabular-nums">{value}</dd>
        </div>
      ))}
    </dl>
  )
}

/** The symbols as they landed, rows top to bottom; the winning cells stand out. */
function SymbolGrid({
  spin,
  paylines,
}: {
  spin: SpinPlay
  paylines: Paylines
}) {
  const { t } = useTranslation()
  const cells = winningCells(spin.result, paylines)
  return (
    <table
      aria-label={t('roundsPage.show.grid.aria')}
      className="border-separate border-spacing-1 text-xs"
    >
      <thead className="sr-only">
        <tr>
          {spin.result.grid.map((_, reel) => (
            <th
              key={reel}
              scope="col"
            >
              {t('roundsPage.show.grid.reel', { number: reel + 1 })}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {gridRows(spin.result.grid).map((symbols, row) => (
          <tr key={row}>
            {symbols.map((symbol, reel) => {
              const isWin = cells.has(cellKey(reel, row))
              return (
                <td
                  key={reel}
                  data-win={isWin || undefined}
                  className={cn(
                    'h-10 w-16 rounded-md border text-center font-mono',
                    isWin
                      ? 'border-primary bg-primary/15 font-semibold'
                      : 'text-muted-foreground',
                  )}
                >
                  {symbol}
                  {isWin && (
                    <span className="sr-only">
                      {' '}
                      {t('roundsPage.show.grid.win')}
                    </span>
                  )}
                </td>
              )
            })}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/** The visible name of a win table, above it. */
function WinCaption({ children }: { children: ReactNode }) {
  return (
    <caption className="text-muted-foreground px-2 pb-1 text-left text-xs font-medium">
      {children}
    </caption>
  )
}

function multiplierText(multiplier: number, t: TFunction) {
  return t('roundsPage.multiplier', { multiplier })
}

/** Payline, scatter and expanding wins of a spin, one table each. */
function SpinWins({ spin }: { spin: SpinPlay }) {
  const { t } = useTranslation()
  const { paylineWins, scatterWins, expandingWin, totalWin } = spin.result
  if (totalWin === 0 && expandingWin === null) {
    return (
      <p className="text-muted-foreground text-sm">
        {t('roundsPage.show.wins.none')}
      </p>
    )
  }
  return (
    <div className="space-y-3">
      {paylineWins.length > 0 && (
        <UiTable
          aria-label={t('roundsPage.show.wins.payline')}
          className="caption-top text-sm"
        >
          <WinCaption>{t('roundsPage.show.wins.payline')}</WinCaption>
          <TableHeader>
            <TableRow>
              <TableHead>{t('roundsPage.show.wins.line')}</TableHead>
              <TableHead>{t('roundsPage.show.wins.symbol')}</TableHead>
              <TableHead>{t('roundsPage.show.wins.count')}</TableHead>
              <TableHead>{t('roundsPage.show.wins.multiplier')}</TableHead>
              <TableHead className="text-right">
                {t('roundsPage.show.wins.win')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paylineWins.map((win) => (
              <TableRow key={win.paylineIndex}>
                <TableCell className="tabular-nums">
                  {win.paylineIndex + 1}
                </TableCell>
                <TableCell className="font-mono">{win.symbolId}</TableCell>
                <TableCell className="tabular-nums">{win.matchCount}</TableCell>
                <TableCell className="tabular-nums">
                  {multiplierText(win.payoutMultiplier, t)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatCredits(win.winAmount)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </UiTable>
      )}
      {scatterWins.length > 0 && (
        <UiTable
          aria-label={t('roundsPage.show.wins.scatter')}
          className="caption-top text-sm"
        >
          <WinCaption>{t('roundsPage.show.wins.scatter')}</WinCaption>
          <TableHeader>
            <TableRow>
              <TableHead>{t('roundsPage.show.wins.symbol')}</TableHead>
              <TableHead>{t('roundsPage.show.wins.count')}</TableHead>
              <TableHead>{t('roundsPage.show.wins.multiplier')}</TableHead>
              <TableHead className="text-right">
                {t('roundsPage.show.wins.win')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {scatterWins.map((win) => (
              <TableRow key={win.symbolId}>
                <TableCell className="font-mono">{win.symbolId}</TableCell>
                <TableCell className="tabular-nums">{win.count}</TableCell>
                <TableCell className="tabular-nums">
                  {multiplierText(win.payoutMultiplier, t)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatCredits(win.winAmount)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </UiTable>
      )}
      {expandingWin !== null && (
        <UiTable
          aria-label={t('roundsPage.show.wins.expanding')}
          className="caption-top text-sm"
        >
          <WinCaption>{t('roundsPage.show.wins.expanding')}</WinCaption>
          <TableHeader>
            <TableRow>
              <TableHead>{t('roundsPage.show.wins.symbol')}</TableHead>
              <TableHead>{t('roundsPage.show.wins.reels')}</TableHead>
              <TableHead>{t('roundsPage.show.wins.multiplier')}</TableHead>
              <TableHead>{t('roundsPage.show.wins.lines')}</TableHead>
              <TableHead className="text-right">
                {t('roundsPage.show.wins.win')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="font-mono">
                {expandingWin.symbolId}
              </TableCell>
              <TableCell className="tabular-nums">
                {expandingWin.reels.map((reel) => reel + 1).join(', ')}
              </TableCell>
              <TableCell className="tabular-nums">
                {multiplierText(expandingWin.payoutMultiplier, t)}
              </TableCell>
              <TableCell className="tabular-nums">
                {expandingWin.lineWins.length}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatCredits(expandingWin.winAmount)}
              </TableCell>
            </TableRow>
          </TableBody>
        </UiTable>
      )}
    </div>
  )
}

/** What a spin did to the feature: games awarded, progress, its win so far. */
function featureFacts(spin: SpinPlay, t: TFunction): [string, ReactNode][] {
  const { feature } = spin
  if (feature === null) {
    return []
  }
  const { featureWin } = feature.state
  const played = t('roundsPage.show.facts.played')
  const progress = progressText(feature.state, t)
  switch (feature.kind) {
    case 'triggered': {
      const fact = featureFactOf(feature, t)
      return [
        [t('roundsPage.show.facts.freeGamesAwarded'), feature.gamesAwarded],
        ...(fact === undefined ? [] : [fact as [string, ReactNode]]),
      ]
    }
    case 'retriggered':
      return [
        [
          t('roundsPage.show.facts.retriggered'),
          t('roundsPage.show.facts.retriggeredValue', {
            count: feature.gamesAwarded,
          }),
        ],
        [played, progress],
        [t('roundsPage.show.facts.featureWinSoFar'), formatCredits(featureWin)],
      ]
    case 'continued':
      return [
        [played, progress],
        [t('roundsPage.show.facts.featureWinSoFar'), formatCredits(featureWin)],
      ]
    case 'completed':
      return [
        [played, progress],
        [t('roundsPage.show.facts.featureWin'), formatCredits(featureWin)],
      ]
  }
}

/** One spin: its grid next to its wins and what it did to the feature. */
function SpinDetail({
  spin,
  paylines,
}: {
  spin: SpinPlay
  paylines: Paylines
}) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-wrap items-start gap-6">
      <SymbolGrid
        spin={spin}
        paylines={paylines}
      />
      <div className="min-w-72 flex-1 space-y-3">
        <Facts
          items={[
            [
              t('roundsPage.show.facts.spinWin'),
              formatCredits(spin.result.totalWin),
            ],
            [
              t('roundsPage.show.facts.stopPositions'),
              spin.result.stopPositions.join(', '),
            ],
            ...featureFacts(spin, t),
          ]}
        />
        <SpinWins spin={spin} />
      </div>
    </div>
  )
}

function FreeGames({
  freeGames,
  paylines,
}: {
  freeGames: SpinPlay[]
  paylines: Paylines
}) {
  const { t } = useTranslation()
  const last = freeGames.at(-1)?.feature ?? null
  const fact = featureFactOf(last, t)
  return (
    <Section
      id="round-free-games"
      title={t('roundsPage.show.freeGames.heading')}
    >
      {last !== null && (
        <Facts
          items={[
            [t('roundsPage.show.facts.played'), progressText(last.state, t)],
            [
              t('roundsPage.show.facts.featureWin'),
              formatCredits(last.state.featureWin),
            ],
            ...(fact === undefined ? [] : [fact as [string, ReactNode]]),
          ]}
        />
      )}
      <ol className="space-y-6">
        {freeGames.map((spin, index) => (
          <li
            key={spin.result.proof.nonce}
            className="border-l-2 pl-4"
          >
            <Section
              id={`round-free-game-${index}`}
              title={freeGameLabel(spin, index, t)}
              level={4}
            >
              <SpinDetail
                spin={spin}
                paylines={paylines}
              />
            </Section>
          </li>
        ))}
      </ol>
    </Section>
  )
}

/** The ladder step by step; its last row the collect, when there was one. */
function Gamble({
  steps,
  collected,
}: {
  steps: GambleStep[]
  collected: number | null
}) {
  const { t } = useTranslation()
  return (
    <Section
      id="round-gamble"
      title={t('roundsPage.show.gamble.heading')}
    >
      <UiTable
        aria-label={t('roundsPage.show.gamble.aria')}
        className="max-w-2xl text-sm"
      >
        <TableHeader>
          <TableRow>
            <TableHead>{t('roundsPage.show.gamble.step')}</TableHead>
            <TableHead>{t('roundsPage.show.gamble.choice')}</TableHead>
            <TableHead>{t('roundsPage.show.gamble.card')}</TableHead>
            <TableHead>{t('roundsPage.show.gamble.result')}</TableHead>
            <TableHead className="text-right">
              {t('roundsPage.show.gamble.ladder')}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {steps.map((step) =>
            step.kind === 'opened' ? (
              <TableRow key="opened">
                <TableCell>{t('roundsPage.show.gamble.gambled')}</TableCell>
                <TableCell />
                <TableCell />
                <TableCell />
                <TableCell className="text-right tabular-nums">
                  {formatCredits(step.total)}
                </TableCell>
              </TableRow>
            ) : (
              <TableRow key={step.attempt}>
                <TableCell>
                  {t('roundsPage.show.gamble.pick', { number: step.attempt })}
                </TableCell>
                <TableCell>
                  {t(`roundsPage.show.gamble.choiceValue.${step.pick.choice}`)}
                </TableCell>
                <TableCell>
                  {t('roundsPage.show.gamble.cardWithColour', {
                    card: t(
                      `roundsPage.show.gamble.cardValue.${step.pick.card}`,
                    ),
                    colour: t(
                      `roundsPage.show.gamble.colourValue.${gambleSymbolToColour(step.pick.card)}`,
                    ),
                  })}
                </TableCell>
                <TableCell>
                  {step.pick.won
                    ? t('roundsPage.show.gamble.won')
                    : t('roundsPage.show.gamble.lost')}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatCredits(step.total ?? 0)}
                </TableCell>
              </TableRow>
            ),
          )}
          {collected !== null && (
            <TableRow>
              <TableCell>{t('roundsPage.show.gamble.collected')}</TableCell>
              <TableCell />
              <TableCell />
              <TableCell />
              <TableCell className="text-right tabular-nums">
                {formatCredits(collected)}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </UiTable>
    </Section>
  )
}

function ledgerAmount({ amount }: RoundLedgerEntry) {
  return formatSignedCredits(amount)
}

function Ledger({ entries }: { entries: RoundLedgerEntry[] }) {
  const { t } = useTranslation()
  return (
    <Section
      id="round-ledger"
      title={t('roundsPage.show.ledger.heading')}
    >
      {entries.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          {t('roundsPage.show.ledger.empty')}
        </p>
      ) : (
        <UiTable
          aria-label={t('roundsPage.show.ledger.aria')}
          className="max-w-2xl text-sm"
        >
          <TableHeader>
            <TableRow>
              <TableHead>{t('roundsPage.show.ledger.time')}</TableHead>
              <TableHead>{t('roundsPage.show.ledger.kind')}</TableHead>
              <TableHead className="text-right">
                {t('roundsPage.show.ledger.amount')}
              </TableHead>
              <TableHead className="text-right">
                {t('roundsPage.show.ledger.balanceAfter')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {entries.map((entry, index) => (
              <TableRow key={index}>
                <TableCell>{formatDateTime(entry.createdAt)}</TableCell>
                <TableCell>
                  {entry.kind === 'bet'
                    ? t('roundsPage.show.ledger.bet')
                    : t('roundsPage.show.ledger.win')}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {ledgerAmount(entry)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatCredits(entry.balanceAfter)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </UiTable>
      )}
    </Section>
  )
}

function FairnessProof({ proofs }: { proofs: SpinProof[] }) {
  const { t } = useTranslation()
  return (
    <Section
      id="round-proof"
      title={t('roundsPage.show.proof.heading')}
    >
      <p className="text-muted-foreground max-w-2xl text-sm">
        {t('roundsPage.show.proof.intro')}
      </p>
      <UiTable
        aria-label={t('roundsPage.show.proof.aria')}
        className="text-sm"
      >
        <TableHeader>
          <TableRow>
            <TableHead>{t('roundsPage.show.proof.draw')}</TableHead>
            <TableHead>{t('roundsPage.show.proof.stream')}</TableHead>
            <TableHead>{t('roundsPage.show.proof.nonce')}</TableHead>
            <TableHead>{t('roundsPage.show.proof.serverSeedHash')}</TableHead>
            <TableHead>{t('roundsPage.show.proof.clientSeed')}</TableHead>
            <TableHead>{t('roundsPage.show.proof.serverSeed')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {proofs.map((proof, index) => (
            <TableRow key={`${proof.stream}:${proof.nonce}`}>
              <TableCell className="tabular-nums">{index + 1}</TableCell>
              <TableCell>
                {t(`roundsPage.show.proof.streamValue.${proof.stream}`)}
              </TableCell>
              <TableCell className="tabular-nums">{proof.nonce}</TableCell>
              <TableCell className="font-mono text-xs break-all whitespace-normal">
                {proof.serverSeedHash}
              </TableCell>
              <TableCell className="font-mono text-xs break-all whitespace-normal">
                {proof.clientSeed}
              </TableCell>
              <TableCell className="text-xs break-all whitespace-normal">
                {proof.serverSeed === null ? (
                  <span className="text-muted-foreground">
                    {t('roundsPage.show.proof.notRevealed')}
                  </span>
                ) : (
                  <span className="font-mono">{proof.serverSeed}</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </UiTable>
    </Section>
  )
}

/**
 * One round of a cabinet (`/cabinets/:cabinetId/rounds/:id`): what it cost
 * and paid out (what is pending while open, what the collect paid out once
 * collected), every spin with its grid and wins (the paid spin, then its
 * free games), the gamble, its ledger entries and the proof of each draw.
 * For staff and the host tenant's owners/admins (the route's `CanAccess`).
 */
export function RoundShow() {
  const { t } = useTranslation()
  const { query } = useShow<RoundDetail, HttpError>({
    resource: ROUNDS_RESOURCE,
  })
  const round = query.data?.data
  const { names: gameNames } = useGameOptions()

  if (query.error?.statusCode === 404) {
    return <NotFoundPage />
  }

  const steps = round === undefined ? undefined : roundSteps(round.outcome)
  const pending = round === undefined ? null : pendingWin(round)

  return (
    <ShowView>
      <ShowViewHeader
        title={
          round === undefined
            ? undefined
            : t('roundsPage.show.title', {
                date: formatDateTime(round.openedAt),
              })
        }
        canEdit={false}
      />
      {query.error && (
        <p
          role="alert"
          className="text-destructive text-sm"
        >
          {t('roundsPage.show.loadError', { message: query.error.message })}
        </p>
      )}
      {round !== undefined && steps !== undefined && (
        <div className="space-y-8">
          <Facts
            items={[
              [
                t('roundsPage.show.facts.cabinet'),
                <CabinetName id={round.cabinetId} />,
              ],
              [
                t('roundsPage.show.facts.game'),
                gameNames.isLoading
                  ? '…'
                  : (gameNames.byId.get(round.gameId) ?? '—'),
              ],
              [
                t('roundsPage.show.facts.configVersion'),
                versionLabel(round.configurationName, round.configVersion),
              ],
              [t('roundsPage.show.facts.stake'), stakeWithTotalText(round, t)],
              [t('roundsPage.show.facts.win'), formatCredits(round.totalWin)],
              ...(pending === null
                ? []
                : [
                    [
                      t('roundsPage.show.facts.pending'),
                      formatCredits(pending),
                    ] as [string, ReactNode],
                  ]),
              ...(steps.collected === null
                ? []
                : [
                    [
                      t('roundsPage.show.facts.collected'),
                      formatCredits(steps.collected),
                    ] as [string, ReactNode],
                  ]),
              [
                t('roundsPage.show.facts.freeGames'),
                formatNumber(round.freeGames),
              ],
              [
                t('roundsPage.show.facts.status'),
                <RoundStatusBadge round={round} />,
              ],
              [
                t('roundsPage.show.facts.opened'),
                formatDateTime(round.openedAt),
              ],
              [
                t('roundsPage.show.facts.closed'),
                formatOptionalDateTime(round.closedAt) ?? '—',
              ],
              [
                t('roundsPage.show.facts.engineRoundId'),
                <code className="font-mono text-xs break-all">
                  {round.engineRoundId}
                </code>,
              ],
            ]}
          />
          {steps.paidSpin !== undefined && (
            <Section
              id="round-paid-spin"
              title={t('roundsPage.show.paidSpin')}
            >
              <SpinDetail
                spin={steps.paidSpin}
                paylines={round.paylines}
              />
            </Section>
          )}
          {steps.freeGames.length > 0 && (
            <FreeGames
              freeGames={steps.freeGames}
              paylines={round.paylines}
            />
          )}
          {steps.gamble.length > 0 && (
            <Gamble
              steps={steps.gamble}
              collected={steps.collected}
            />
          )}
          <Ledger entries={round.ledger} />
          <FairnessProof proofs={round.proof} />
        </div>
      )}
    </ShowView>
  )
}
